import { TuiTable } from '@taiga-ui/addon-table';
import { TuiChip, TuiSkeleton } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  OutputRefSubscription,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IsLoadingService, XAlertService } from '@core/services';
import { RequestChangeHistoryService } from '@core/services/request/request-change-history.service';
import {
  HistoryLogTabs,
  RequestLog,
  RequestLogAction,
} from '@core/types/RequestLog';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyBlockComponent } from '@core/components';
import { WithCreatedAgo } from '@core/helpers';
import {
  TuiDialogContext,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiDialogService,
} from '@taiga-ui/core';
import {
  RequestHistoryLogsTranslations,
  RequestHistoryLogsTranslationsPayload,
} from '@core/types';
import { catchError } from 'rxjs/operators';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';

type SortBy = 'action' | 'context' | 'userId.fullname' | 'createdAt';
enum SortedDirectionEnum {
  Asc = 'asc',
  Desc = 'desc',
}

@Component({
  selector: 'x-change-history',
  imports: [
    TuiSkeleton,
    CommonModule,
    TuiTable,
    TranslatePipe,
    EmptyBlockComponent,
    TuiHint,
    TuiChip,
    TuiIcon,
    TuiButton,
  ],
  templateUrl: './change-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeHistoryComponent implements OnDestroy, OnInit {
  $showRequestHistoryLogs = input<OutputEmitterRef<boolean>>(undefined, {
    alias: 'showRequestHistoryLogs',
  });

  $historyLogsTabChange = input<OutputEmitterRef<HistoryLogTabs>>(undefined, {
    alias: 'historyLogsTabChange',
  });

  $historyLogsSearchChanged = input<OutputEmitterRef<string>>(undefined, {
    alias: 'historyLogsSearchChanged',
  });

  $requestHistoryLogsCounterOutput = output<number>();

  public requestHistoryLogs: WithCreatedAgo<RequestLog>[] = [];
  public $filteredHistoryLogs = signal<WithCreatedAgo<RequestLog>[]>([]);
  public showLogs = false;
  public searchText = '';
  public $comparingLogsData = signal<string>('');
  public currentTab: HistoryLogTabs = HistoryLogTabs.Last15;
  private changedComparingAlgorithm = false;
  public RequestLogActionEnum = RequestLogAction;
  public $sortedBy: WritableSignal<SortBy> = signal('createdAt');
  public $sortedDirection: WritableSignal<SortedDirectionEnum> = signal(
    SortedDirectionEnum.Desc,
  );

  public isInitialLoad = true;
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly xAlertService = inject(XAlertService);
  private requestChangeHistoryService = inject(RequestChangeHistoryService);
  private readonly destroyRef = inject(DestroyRef);
  private subscriptions: OutputRefSubscription[] = [];

  translates: RequestHistoryLogsTranslationsPayload = {
    [RequestHistoryLogsTranslations.DialogComparingLogsError]:
      'Ошибка сравнения логов',
  };

  readonly tableColumns = [
    'table_col_action',
    'table_col_user',
    'table_col_info',
    'table_col_when',
  ];

  constructor() {
    effect(() => {
      const emitter = this.$showRequestHistoryLogs();

      if (emitter) {
        this.subscriptions.push(
          emitter.subscribe(show => {
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
            }
            this.showLogs = show;
            if (show) {
              this.getHistoryLogs();
            }
            if (!this.isInitialLoad && show) {
              this.requestChangeHistoryService.refreshEntities();
            }
          }),
        );
      }
    });

    effect(() => {
      const emitter = this.$historyLogsTabChange();

      if (emitter) {
        this.subscriptions.push(
          emitter.subscribe(tab => {
            this.currentTab = tab;
            this.filterLogsByTab();
          }),
        );
      }
    });

    effect(() => {
      const emitter = this.$historyLogsSearchChanged();

      if (emitter) {
        this.subscriptions.push(
          emitter.subscribe(searchText => {
            this.searchText = searchText || '';
            this.$filteredHistoryLogs.set(this.filterLogsBySearchText());
          }),
        );
      }
    });
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestHistoryLogsTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  getHistoryLogs() {
    this.requestChangeHistoryService.entities$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(logs => {
        this.requestHistoryLogs = logs;
        if (logs.length) {
          this.$requestHistoryLogsCounterOutput.emit(logs.length);
        }
        this.filterLogsByTab();
      });
  }

  filterLogsBySearchText() {
    if (!this.searchText) {
      return this.requestHistoryLogs;
    }
    const lowerSearchText = this.searchText.toLowerCase();

    return this.requestHistoryLogs.filter(
      log =>
        log.context.toLowerCase().includes(lowerSearchText) ||
        log.userId.fullname.toLowerCase().includes(lowerSearchText),
    );
  }

  private filterLogsByTab() {
    if (this.currentTab === HistoryLogTabs.Last15) {
      const last15Logs = this.requestHistoryLogs.slice(0, 15);

      if (this.searchText) {
        this.$filteredHistoryLogs.set(
          last15Logs.filter(
            log =>
              log.context
                .toLowerCase()
                .includes(this.searchText.toLowerCase()) ||
              log.userId.fullname
                .toLowerCase()
                .includes(this.searchText.toLowerCase()),
          ),
        );
      } else {
        this.$filteredHistoryLogs.set(last15Logs);
      }
    } else {
      if (this.searchText) {
        this.$filteredHistoryLogs.set(this.filterLogsBySearchText());
      } else {
        this.$filteredHistoryLogs.set(this.requestHistoryLogs);
      }
    }
  }

  public sort(sortBy: SortBy): void {
    const currentSortBy = this.$sortedBy();
    const currentSortDirection = this.$sortedDirection();

    if (currentSortBy === sortBy) {
      this.$sortedDirection.set(
        currentSortDirection === SortedDirectionEnum.Asc
          ? SortedDirectionEnum.Desc
          : SortedDirectionEnum.Asc,
      );
    } else {
      this.$sortedBy.set(sortBy);
      this.$sortedDirection.set(SortedDirectionEnum.Asc);
    }

    const sortedLogs = [...this.$filteredHistoryLogs()].sort((a, b) => {
      const aValue = a[sortBy as keyof WithCreatedAgo<RequestLog>];
      const bValue = b[sortBy as keyof WithCreatedAgo<RequestLog>];

      if (aValue === null || bValue === null) {
        if (aValue === bValue) {
          return 0;
        }

        return aValue === null ? 1 : -1;
      }

      if (aValue < bValue) {
        return this.$sortedDirection() === SortedDirectionEnum.Asc ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.$sortedDirection() === SortedDirectionEnum.Asc ? 1 : -1;
      }

      return 0;
    });

    this.$filteredHistoryLogs.set(sortedLogs);
  }

  public compareLogModal(
    logId: RequestLog['id'],
    content: PolymorpheusContent<TuiDialogContext>,
  ): void {
    this.changedComparingAlgorithm = false;
    this.requestChangeHistoryService
      .compareLogs(logId)
      .pipe(
        catchError(error => {
          console.error('Error comparing logs:', error);
          this.xAlertService.error(
            this.translates[
              RequestHistoryLogsTranslations.DialogComparingLogsError
            ],
          );

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(data => {
        if (!data) {
          this.xAlertService.error(
            this.translates[
              RequestHistoryLogsTranslations.DialogComparingLogsError
            ],
          );
        } else {
          this.$comparingLogsData.set(data);
          this.dialogService$
            .open(content, {
              appearance: 'taiga logComparisonModal',
              size: null,
              closable: true,
              dismissible: true,
            })
            .subscribe();
        }
      });
  }

  public changeComparingLogsAlgorithm(): void {
    this.changedComparingAlgorithm = !this.changedComparingAlgorithm;
    this.requestChangeHistoryService
      .changeComparingLogsAlgorithm(this.changedComparingAlgorithm)
      .subscribe(data => {
        if (!data) {
          this.xAlertService.error(
            this.translates[
              RequestHistoryLogsTranslations.DialogComparingLogsError
            ],
          );
        } else {
          this.$comparingLogsData.set(data);
        }
      });
  }
}
